//SplashView.js - Corrected Version with Fixed Close Button
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, MessageSquare, Users, BarChart2, Menu, X, Check, ChevronLeft, Megaphone, FileCode, MessageSquareCode, ArrowRight } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';

import SmoothFdbkAnimation from '../../components/AnimatedFdbk/AnimatedFdbk';
import HeroCarousel from '../../components/HeroCarousel/HeroCarousel';
import Button from '../../components/Button/Button';
import DemoRequestModal from '../../components/SplashView/DemoRequestModal/DemoRequestModal';
import ContactRequestModal from '../../components/SplashView/ContactRequestModal/ContactRequestModal';
import PrivacyPolicyModal from '../../components/SplashView/PrivacyPolicyModal/PrivacyPolicyModal';
import TermsOfServiceModal from '../../components/SplashView/TermsOfServiceModal/TermsOfServiceModal';

import './SplashView.css';
import img1 from '../../images/face1.gif';
import img2 from '../../images/face2.gif';
import img3 from '../../images/face3.gif';
import img4 from '../../images/face4.gif';
import img5 from '../../images/face5.gif';
import exclamationImg from '../../images/exclamation-mark.png';
import googleImg from '../../images/google.png';

// Main component for the fdbk splash page
const FdbkSplash = () => {
  const [showEmoji, setShowEmoji] = useState(1);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCheckingAuth, setCheckingAuth] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const [isYearlyPricing, setIsYearlyPricing] = useState(false);
  const [isPricingTransitioning, setIsPricingTransitioning] = useState(false);
  const [featuresCarouselIndex, setFeaturesCarouselIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();
  
  const scoreGifs = {
    1: img1,
    2: img2,
    3: img3,
    4: img4,
    5: img5,
    'exclamation': exclamationImg,
    'google': googleImg
  };

  // Pricing data
  const pricingPlans = [
    {
      name: "Counter Kiosk",
      description: "Perfect for countertop placement in smaller spaces",
      monthlyPrice: 89,
      yearlyPrice: 81,
      installationCost: 79,
      kioskType: "Compact countertop design",
      category: "physical",
      popular: false
    },
    {
      name: "Standard Kiosk",
      description: "Ideal for most businesses seeking comprehensive feedback",
      monthlyPrice: 119,
      yearlyPrice: 99,
      installationCost: 99,
      kioskType: "Full-size kiosk display",
      category: "physical",
      popular: true
    },
    {
      name: "Deluxe Kiosk",
      description: "Premium solution for high-traffic locations",
      monthlyPrice: 249,
      yearlyPrice: 227,
      installationCost: 155,
      kioskType: "Premium kiosk display",
      category: "physical",
      popular: false
    },
    {
      name: "Virtual Standard",
      description: "Digital feedback collection for multiple touchpoints",
      monthlyPrice: 990,
      yearlyPrice: 900,
      installationCost: 0,
      category: "virtual",
      popular: false,
      features: ["5 Kiosks", "QR code access"]
    },
    {
      name: "Virtual Deluxe",
      description: "Enterprise-grade virtual feedback ecosystem",
      monthlyPrice: 1500,
      yearlyPrice: 1365,
      installationCost: 0,
      category: "virtual",
      popular: false,
      features: ["10 Kiosks", "QR code access"]
    }
  ];

  // Features carousel data
  const allFeatures = [
    { name: "Real-time feedback collection", description: "Capture customer responses instantly as they happen, ensuring no valuable insights are lost." },
    { name: "Dynamic follow-up questions", description: "Automatically ask relevant follow-up questions based on responses to gather deeper insights." },
    { name: "Advanced analytics & reporting", description: "Transform raw feedback into actionable insights with powerful reporting tools." },
    { name: "Custom follow-up questions", description: "Create personalized questions that align with your specific business objectives." },
    { name: "Export capabilities", description: "Download and analyze your data in multiple formats for deeper analysis." },
    { name: "Team collaboration tools", description: "Share insights and collaborate with your team to drive meaningful improvements." },
  ];

  const itemsPerPage = 3;
  const totalPages = Math.ceil(allFeatures.length / itemsPerPage);

  // Body scroll lock utility
  useEffect(() => {
    if (isMobileMenuOpen) {
      // Store current scroll position
      const scrollY = window.pageYOffset;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.body.classList.add('menu-open');
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.classList.remove('menu-open');
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.classList.remove('menu-open');
    };
  }, [isMobileMenuOpen]);
  
  // Rotate through emojis and questions
  useEffect(() => {
    const emojiInterval = setInterval(() => {
      setShowEmoji((prev) => (prev + 1) % 5);
    }, 2000);
    
    return () => {
      clearInterval(emojiInterval);
    };
  }, []);
  
  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      setCheckingAuth(true);
      
      try {
        const authSession = await fetchAuthSession();
        console.log('Auth session check succeeded');
        setIsLoggedIn(true);
      } catch (error) {
        console.log('Not currently authenticated in Cognito', error);
        setIsLoggedIn(false);
        
        if (localStorage.getItem('user')) {
          console.log('Clearing stale user from localStorage');
          localStorage.removeItem('user');
        }
      } finally {
        setCheckingAuth(false);
      }
    };
    
    checkAuth();
  }, []);

  // Fixed click outside handler with delay
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close if menu is open AND click is outside both menu and button
      if (isMobileMenuOpen && 
          !event.target.closest('.mobile-menu') && 
          !event.target.closest('.mobile-menu-button')) {
        setIsMobileMenuOpen(false);
      }
    };

    // Add delay to prevent immediate closure when opening
    if (isMobileMenuOpen) {
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
    
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobileMenuOpen]);

  // retrieve device_id from URL and store it in localStorage
  function saveDeviceIdFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      console.log('URL params:', params.toString());
      const deviceId = params.get('device_id');
  
      // Remove old device_id if it exists
      localStorage.removeItem('device_id');
  
      // Store new device_id if found
      if (deviceId) {
        localStorage.setItem('device_id', deviceId);
      }
  
      return deviceId;
    } catch (error) {
      console.error('Error handling device_id from URL:', error);
      return null;
    }
  }

  // Check if user is already authenticated
  useEffect(() => {
    // retieve device_id from URL and store it in localStorage
    const deviceId = saveDeviceIdFromUrl();

    const checkAuth = async () => {
      setCheckingAuth(true);
      
      try {
        const authSession = await fetchAuthSession();
        
        if (authSession && authSession.tokens && authSession.tokens.accessToken) {
          console.log('User is already authenticated');
          if (deviceId) {
            // If deviceId is present, navigate to DeviceSetup
            navigate('/DeviceSetup?device_id=' + deviceId);
          }
          
          navigate('/');

        } else {
          console.log('No valid tokens found');
          localStorage.removeItem('user');
        }
      } catch (error) {
        console.log('Not currently authenticated:', error.message);
        localStorage.removeItem('user');
      } finally {
        setCheckingAuth(false);
      }
    };
    
    checkAuth();
  }, [navigate]);

  // Handle demo request
  const handleDemoRequest = () => {
    setIsDemoModalOpen(true);
  };

  const handleContactRequest = () => {
    setIsContactModalOpen(true);
  };

  const handlePrivacyRequest = () => {
    setIsPrivacyModalOpen(true);
  };

  const handleTermsRequest = () => {
    setIsTermsModalOpen(true);
  };

  // Handle pricing toggle with smooth transition
  const handlePricingToggle = () => {
    if (isPricingTransitioning) return;
    
    setIsPricingTransitioning(true);
    
    // Start fade out
    setTimeout(() => {
      setIsYearlyPricing(!isYearlyPricing);
      
      // Fade back in after content changes
      setTimeout(() => {
        setIsPricingTransitioning(false);
      }, 150);
    }, 150);
  };

  // Handle features carousel navigation with smooth transitions
  const nextFeaturesSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setFeaturesCarouselIndex((prev) => (prev + 1) % totalPages);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  const prevFeaturesSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setFeaturesCarouselIndex((prev) => (prev - 1 + totalPages) % totalPages);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  const goToSlide = (index) => {
    if (isTransitioning || index === featuresCarouselIndex) return;
    setIsTransitioning(true);
    setFeaturesCarouselIndex(index);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  // Fixed mobile menu toggle
  const handleMobileMenuToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Enhanced mobile menu item click handler
  const handleMobileMenuItemClick = (action, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    setIsMobileMenuOpen(false);
    
    // Small delay before scrolling to ensure menu closes first
    setTimeout(() => {
      if (action === 'features') {
        document.querySelector('.features-section')?.scrollIntoView({ behavior: 'smooth' });
      } else if (action === 'pricing') {
        document.querySelector('.pricing-section')?.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  };

  // Enhanced Get Started handler
  const handleGetStarted = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    setIsMobileMenuOpen(false);
    
    setTimeout(() => {
      if (isLoggedIn) {
        navigate('/Site');
      } else {
        navigate('/login');
      }
    }, 100);
  };

  const handleGoToDashboard = () => {
    navigate(isLoggedIn ? '/Site' : '/login');
  };

  // Fixed close menu handler
  const handleCloseMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMobileMenuOpen(false);
  };

  // Pricing content component with transition wrapper
  const PricingCardContent = ({ plan }) => (
    <div className={`pricing-content-transition ${isPricingTransitioning ? 'pricing-fade-out' : 'pricing-fade-in'}`}>
      <div className="mb-4">
        <div className="pricing-price-container">
          <span className="pricing-price-number text-3xl sm:text-4xl font-bold text-gray-800">
            ${isYearlyPricing ? plan.yearlyPrice : plan.monthlyPrice}
          </span>
          <span className="text-gray-600 text-sm sm:text-base ml-1">
            /month
          </span>
        </div>
        {isYearlyPricing && (
          <div className="text-xs sm:text-sm text-gray-500 mt-1 pricing-yearly-detail">
            Billed annually (${(plan.yearlyPrice * 12).toFixed(0)}/year)
          </div>
        )}
        <div className="text-sm text-indigo-600 mt-2 font-medium">
          + ${plan.installationCost} installation per unit
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex flex-col items-center overflow-x-hidden">
      
      {/* Demo Request Modal */}
      <DemoRequestModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />
      <ContactRequestModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />

      <PrivacyPolicyModal
        isOpen={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
      />

      <TermsOfServiceModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />

      {/* Navigation */}
      <nav className="w-full py-4 px-4 sm:py-6 sm:px-8 flex justify-between items-center relative z-50">
        <div className="flex items-center">
          <SmoothFdbkAnimation className="w-8 h-8 sm:w-10 sm:h-10 mr-2" />
        </div>
        
        {/* Desktop Navigation */}
        <div className="hidden lg:flex space-x-6 items-center">
          <Button
            variant="ghost"
            size="medium"
            onClick={() => handleMobileMenuItemClick('features')}
          >
            Features
          </Button>
          <Button
            variant="ghost"
            size="medium"
            onClick={() => handleMobileMenuItemClick('pricing')}
          >
            Pricing
          </Button>
          
          <Button
            variant="primary"
            size="large"
            icon={<ArrowRight className="w-4 h-4 ml-2" />}
            style={{ width: '450px' }}
            iconPosition="right"
            onClick={handleGetStarted}
            disabled={isCheckingAuth}
          >
            {isCheckingAuth ? 'Loading...' : (isLoggedIn ? "Dashboard" : "Get Started")}
          </Button>
        </div>

        {/* Fixed Mobile Menu Button - Only hamburger/menu icon has pointer-events disabled */}
        <button
          className="lg:hidden mobile-menu-button p-3 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
          onClick={handleMobileMenuToggle}
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMobileMenuOpen}
          style={{ 
            minWidth: '48px', 
            minHeight: '48px',
            WebkitTapHighlightColor: 'transparent',
            zIndex: 10001
          }}
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6 text-gray-700" style={{ pointerEvents: 'none' }} />
          ) : (
            <Menu className="w-6 h-6 text-gray-700" style={{ pointerEvents: 'none' }} />
          )}
        </button>

        {/* Fixed Mobile Menu */}
        {isMobileMenuOpen && (
          <>
            {/* Full Screen Backdrop */}
            <div 
              className="lg:hidden fixed inset-0 bg-black bg-opacity-60 z-40 mobile-menu-overlay"
              onClick={handleCloseMenu}
              style={{ touchAction: 'none' }}
            />
            
            {/* Full Width Menu Panel */}
            <div className="lg:hidden mobile-menu fixed top-0 left-0 right-0 bottom-0 bg-white shadow-2xl z-50 overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <SmoothFdbkAnimation className="w-8 h-8 mr-2" />
                  </div>
                  <button
                    onClick={handleCloseMenu}
                    className="p-2 rounded-lg hover:bg-gray-100"
                    style={{ 
                      minWidth: '48px', 
                      minHeight: '48px',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                    aria-label="Close menu"
                  >
                    {/* Close button X icon - NO pointer-events disabled */}
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <button
                  className="w-full p-4 text-left text-lg rounded-lg hover:bg-gray-100 transition-colors"
                  onClick={(e) => handleMobileMenuItemClick('features', e)}
                  style={{ 
                    minHeight: '56px',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  Features
                </button>
                
                <button
                  className="w-full p-4 text-left text-lg rounded-lg hover:bg-gray-100 transition-colors"
                  onClick={(e) => handleMobileMenuItemClick('pricing', e)}
                  style={{ 
                    minHeight: '56px',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  Pricing
                </button>
                
                <div className="pt-4 border-t border-gray-200">
                  <button
                    className="w-full p-4 bg-indigo-600 text-white text-lg rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                    onClick={handleGetStarted}
                    disabled={isCheckingAuth}
                    style={{ 
                      minHeight: '56px',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                  >
                    {isCheckingAuth ? 'Loading...' : (isLoggedIn ? "Dashboard" : "Get Started")}
                    <ChevronRight className="w-5 h-5 ml-2" style={{ pointerEvents: 'none' }} />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </nav>

      {/* Hero Section with Carousel */}
      <div className="flex flex-col lg:flex-row justify-between items-center w-full max-w-7xl px-4 sm:px-8 py-8 sm:py-12 gap-8 lg:gap-12">
        <div className="w-full lg:w-2/5 mb-8 lg:mb-0" style={{ paddingLeft: '8rem'}}>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 leading-tight mb-4 sm:mb-6 text-center lg:text-left">
            Customer feedback that <span style={{ color: '#667eea' }}>evolves</span> with your business
          </h1>
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 mb-6 sm:mb-8 text-center lg:text-left max-w-2xl mx-auto lg:mx-0">
            Dynamic, tablet-based feedback system that adapts to your customers' responses in real-time.
          </p>
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 justify-center lg:justify-start">
            <Button
              variant="primary"
              size="large"
              fullWidth
              className="sm:w-auto"
              onClick={handleDemoRequest}
            >
              Request Demo
            </Button>
            <Button
              variant="outline"
              size="large"
              fullWidth
              className="sm:w-auto"
              onClick={() => {
                document.querySelector('.features-section')?.scrollIntoView({ 
                  behavior: 'smooth' 
                });
              }}
            >
              Learn More
            </Button>
          </div>
        </div>
        
        {/* Carousel Section */}
        <div className="w-full lg:w-3/5 flex justify-center lg:justify-end">
          <div className="w-full max-w-2xl lg:max-w-4xl">
            <HeroCarousel scoreGifs={scoreGifs} />
          </div>
        </div>
      </div>
      
      {/* Features Section */}
      <div className="w-full bg-white py-12 sm:py-16 features-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-800 mb-8 sm:mb-12">
            How fdbk elevates your customer insights
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12">
            <div className="bg-indigo-50 p-4 sm:p-6 rounded-xl">
              <div className="bg-indigo-100 p-3 rounded-lg inline-block mb-4">
                <MessageSquare className="text-indigo-600 w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3">Dynamic Follow-ups</h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Ask relevant questions based on initial responses, diving deeper into customer experiences.
              </p>
            </div>
            
            <div className="bg-indigo-50 p-4 sm:p-6 rounded-xl">
              <div className="bg-indigo-100 p-3 rounded-lg inline-block mb-4">
                <Users className="text-indigo-600 w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3">User-Generated Questions</h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Empower your team to create custom questions based on current business needs.
              </p>
            </div>
            
            <div className="bg-indigo-50 p-4 sm:p-6 rounded-xl">
              <div className="bg-indigo-100 p-3 rounded-lg inline-block mb-4">
                <BarChart2 className="text-indigo-600 w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3">Actionable Analytics</h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Transform feedback into clear insights with our powerful reporting dashboard.
              </p>
            </div>
          </div>
          <br></br>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12">
            <div className="bg-indigo-50 p-4 sm:p-6 rounded-xl">
              <div className="bg-indigo-100 p-3 rounded-lg inline-block mb-4">
                <FileCode className="text-indigo-600 w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3">Exportable Data Reporting</h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Easily export your feedback data for in-depth analysis and reporting.
              </p>
            </div>
            
            <div className="bg-indigo-50 p-4 sm:p-6 rounded-xl">
              <div className="bg-indigo-100 p-3 rounded-lg inline-block mb-4">
                <MessageSquareCode className="text-indigo-600 w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3">Google Review Connector</h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Seamlessly integrate Google Reviews into your feedback workflow.
              </p>
            </div>
            
            <div className="bg-indigo-50 p-4 sm:p-6 rounded-xl">
              <div className="bg-indigo-100 p-3 rounded-lg inline-block mb-4">
                <Megaphone className="text-indigo-600 w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3">Custom Advertising</h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Advertising space for you or your customers to promote their products or services.
              </p>
            </div>
          </div>

        </div>
      </div>
      
      {/* Pricing Section */}
      <div className="pricing-section">
        <div className="pricing-container">
          <div className="pricing-header">
            <h2 className="pricing-title">
              Choose the perfect plan for your business
            </h2>
            <p className="pricing-subtitle">
              From physical kiosks to virtual feedback collection - we've got you covered
            </p>
            
            {/* Pricing Toggle */}
            <div className="pricing-toggle-wrapper">
              <span className={`pricing-toggle-label ${!isYearlyPricing ? 'active' : ''}`}>
                Monthly
              </span>
              <button
                onClick={handlePricingToggle}
                disabled={isPricingTransitioning}
                className={`pricing-toggle-switch ${isYearlyPricing ? 'yearly' : 'monthly'} ${isPricingTransitioning ? 'updating' : ''}`}
                aria-label="Toggle pricing"
              >
                <span className="pricing-toggle-knob" />
              </button>
              <span className={`pricing-toggle-label ${isYearlyPricing ? 'active' : ''}`}>
                Yearly
              </span>
              {isYearlyPricing && (
                <span className="pricing-save-badge">
                  Save 9%
                </span>
              )}
            </div>
          </div>
          
          {/* Physical Kiosks Section */}
          <div className="pricing-category-section">
            <h3 className="pricing-category-title">Physical Kiosks</h3>
            <div className="pricing-grid physical-grid">
              {pricingPlans.filter(plan => plan.category === 'physical').map((plan) => (
                <div
                  key={plan.name}
                  className={`pricing-card physical-card ${plan.popular ? 'popular' : ''} ${isPricingTransitioning ? 'updating' : ''}`}
                >
                  {plan.popular && (
                    <div className="pricing-card-badge">
                      Most Popular
                    </div>
                  )}
                  
                  <div className="pricing-card-header">
                    <h4 className="pricing-card-title">{plan.name}</h4>
                    <p className="pricing-card-description">{plan.description}</p>
                    
                    <div className={`pricing-card-price ${isPricingTransitioning ? 'fade-out' : 'fade-in'}`}>
                      <div className="pricing-price-container">
                        <span className="pricing-price-number">
                          ${isYearlyPricing ? plan.yearlyPrice : plan.monthlyPrice}*
                        </span>
                        <span className="pricing-price-period">/month</span>
                      </div>
                      {isYearlyPricing && (
                        <div className="pricing-yearly-detail">
                          Billed annually (${(plan.yearlyPrice * 12).toFixed(0)}*/year)
                        </div>
                      )}
                      <div className="pricing-installation-cost">
                        + ${plan.installationCost}* installation per unit
                      </div>
                    </div>
                    
                    <div className="pricing-kiosk-type physical-type">
                      <span>{plan.kioskType}</span>
                    </div>
                  </div>
                  
                  <div className="pricing-card-footer">
                    <Button
                      variant="outline"
                      size="large"
                      fullWidth
                      onClick={handleContactRequest}
                    >
                      Get in Contact
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Virtual Kiosks Section */}
          <div className="pricing-category-section virtual-section">
            <h3 className="pricing-category-title virtual-title">Virtual Kiosks</h3>
            <div className="pricing-grid virtual-grid">
              {pricingPlans.filter(plan => plan.category === 'virtual').map((plan) => (
                <div
                  key={plan.name}
                  className={`pricing-card virtual-card ${plan.popular ? 'popular' : ''} ${isPricingTransitioning ? 'updating' : ''}`}
                >
                  {plan.popular && (
                    <div className="pricing-card-badge virtual-badge">
                      Most Popular
                    </div>
                  )}
                  
                  <div className="pricing-card-header">
                    <h4 className="pricing-card-title">{plan.name}</h4>
                    <p className="pricing-card-description">{plan.description}</p>
                    
                    <div className={`pricing-card-price ${isPricingTransitioning ? 'fade-out' : 'fade-in'}`}>
                      <div className="pricing-price-container">
                        <span className="pricing-price-number">
                          ${isYearlyPricing ? plan.yearlyPrice : plan.monthlyPrice}*
                        </span>
                        <span className="pricing-price-period">/month</span>
                      </div>
                      {isYearlyPricing && (
                        <div className="pricing-yearly-detail">
                          Billed annually (${(plan.yearlyPrice * 12).toFixed(0)}*/year)
                        </div>
                      )}
                      <div className="pricing-installation-cost virtual-no-install">
                        No installation required
                      </div>
                    </div>
                    
                    {/* <div className="pricing-kiosk-type virtual-type">
                      <span>{plan.kioskType}</span>
                    </div> */}
                    
                    {plan.features && (
                      <div className="pricing-features-list">
                        <h5 className="pricing-features-title">Key Features:</h5>
                        <ul className="pricing-features">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="pricing-feature-item">
                              <Check className="pricing-feature-icon" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  <div className="pricing-card-footer">
                    <Button
                      variant={plan.popular ? "primary" : "outline"}
                      size="large"
                      fullWidth
                      onClick={handleContactRequest}
                    >
                      {plan.popular ? "Get Started" : "Get in Contact"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="pricing-footer">
            <p className="pricing-footer-text">
              Physical kiosks include professional installation. Virtual kiosks are ready to deploy immediately.
            </p>
            <p className="pricing-footer-text" style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
              * All prices include GST
            </p>
            <Button
              variant="ghost"
              size="medium"
              onClick={handleContactRequest}
            >
              Questions about pricing? Contact our sales team
            </Button>
          </div>
        </div>
      </div>
      
      {/* CTA Section */}
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
        <div className="bg-indigo-600 rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">
            Ready to transform your customer feedback?
          </h2>
          <p className="text-lg sm:text-xl text-indigo-100 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Join companies that are improving their product and services with fdbk's dynamic feedback system.
          </p>
          
          <Button
            variant="primary"
            size="large"
            style={{
              maxWidth: '85vw',
              border: '1px solid white',
              backgroundColor: 'transparent',
              color: 'white'
            }}
            onClick={isLoggedIn ? handleGoToDashboard : handleDemoRequest}
            className="hover:bg-indigo-650 w-full sm:w-auto"
          >
            {isLoggedIn ? "Go to Dashboard" : "Schedule Your Demo Today"}
          </Button>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="w-full bg-gray-50 py-8 sm:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-center md:text-left">
            <span className="text-xl sm:text-2xl font-bold text-indigo-600">fdbk</span>
            <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">© 2025 fdbk Pty Ltd. All rights reserved.</p>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <Button
              variant="outline"
              size="small"
              onClick={() => {
                handlePrivacyRequest();
              }}
            >
              Privacy
            </Button>
            <Button
              variant="outline"
              size="small"
              onClick={() => {
                handleTermsRequest();
              }}
            >
              Terms
            </Button>
            <Button
              variant="outline"
              size="small"
              onClick={() => {
                handleContactRequest();
              }}
            >
              Contact
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FdbkSplash;